<template>
  <v-menu v-model="isOpen" offset-y left :close-on-content-click="false">
    <template v-slot:activator="{ on }">
      <transition name="fade" mode="out-in">
        <v-btn v-show="showFilter" text color="primary" class="px-2" v-on="on" @click="triggerOpen">
          <v-icon small>mdi-tune</v-icon>
          <span class="ml-2">
            {{ $t("resources.ab_tests.customize_columns.title") }}
          </span>
        </v-btn>
      </transition>
    </template>

    <div class="dynamic-columns-wrapper">
      <div class="dynamic-columns-title">
        <span>{{ $t("resources.ab_tests.customize_columns.title") }}</span>
        <v-icon small @click="isOpen = false">mdi-close</v-icon>
      </div>

      <v-list class="dynamic-columns-list">
        <v-list-item
          v-for="(item, index) in columns"
          :key="index"
          :disabled="item.required"
          :class="{ 'v-list-item--active': item.active && !item.required }"
          @click="!item.required && toggleColumn(index)">
          <v-list-item-action>
            <v-checkbox :input-value="columns[index].active" :disabled="item.required" color="primary" readonly />
          </v-list-item-action>
          <v-list-item-content>
              {{ item.label }}
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </div>
  </v-menu>
</template>

<script>
  import { DATA_SOURCE } from "@/constant/abTest";

  export default {
    name: "AbTestDynamicColumns",
    props: {
      showCallsData: {
        type: Boolean,
        default: true,
      },
      showVoluumData: {
        type: Boolean,
        default: true,
      },
      value: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        isOpen: false,
        columns: [],
      };
    },
    computed: {
      showFilter() {
        return this.showCallsData || this.showVoluumData;
      },
      availableColumns() {
        const columns = {
          name: {
            label: this.$t("resources.ab_tests.customize_columns.name"),
            required: true,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.CALLS, DATA_SOURCE.VOLUUM],
          },
          sessions: {
            label: this.$t(
              `resources.ab_tests.customize_columns.sessions${
                this.showCallsData && this.showVoluumData ? "_combined" : ""
              }`,
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.CALLS, DATA_SOURCE.VOLUUM],
          },
          visits: {
            label: this.$t(
              "resources.ab_tests.customize_columns.visits" +
                (this.showCallsData && this.showVoluumData ? "_voluum" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.VOLUUM],
          },
          uniqueVisits: {
            label: this.$t(
              "resources.ab_tests.customize_columns.unique_visits" +
                (this.showCallsData && this.showVoluumData ? "_voluum" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.VOLUUM],
          },
          conversions: {
            label: this.$t(
              "resources.ab_tests.customize_columns.conversions" +
                (this.showCallsData && this.showVoluumData ? "_voluum" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.VOLUUM],
          },
          calls: {
            label: this.$t(
              "resources.ab_tests.customize_columns.calls" +
                (this.showCallsData && this.showVoluumData ? "_calls" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.CALLS],
          },
          uniqueCallers: {
            label: this.$t(
              "resources.ab_tests.customize_columns.unique_callers" +
                (this.showCallsData && this.showVoluumData ? "_calls" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.CALLS],
          },
          noConnect: {
            label: this.$t(
              "resources.ab_tests.customize_columns.no_connect" +
                (this.showCallsData && this.showVoluumData ? "_calls" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.CALLS],
          },
          callsCr: {
            label: this.$t(
              "resources.ab_tests.customize_columns.calls_cr" +
                (this.showCallsData && this.showVoluumData ? "_calls" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.CALLS],
          },
          sessionsCr: {
            label: this.$t(
              "resources.ab_tests.customize_columns.sessions_cr" +
                (this.showCallsData && this.showVoluumData ? "_calls" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.CALLS],
          },
          revenue: {
            label: this.$t(
              "resources.ab_tests.customize_columns.revenue" +
                (this.showCallsData && this.showVoluumData ? "_combined" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.CALLS, DATA_SOURCE.VOLUUM],
          },
          rpc: {
            label: this.$t(
              "resources.ab_tests.customize_columns.rpc" +
                (this.showCallsData && this.showVoluumData ? "_calls" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.CALLS],
          },
          acl: {
            label: this.$t(
              "resources.ab_tests.customize_columns.acl" +
                (this.showCallsData && this.showVoluumData ? "_calls" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.CALLS],
          },
          epv: {
            label: this.$t(
              "resources.ab_tests.customize_columns.epv" +
                (this.showCallsData && this.showVoluumData ? "_voluum" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.VOLUUM],
          },
          cv: {
            label: this.$t(
              "resources.ab_tests.customize_columns.cv" + (this.showCallsData && this.showVoluumData ? "_voluum" : ""),
            ),
            required: false,
            sources: [DATA_SOURCE.MERGED, DATA_SOURCE.VOLUUM],
          },
        };

        const currentMode =
          this.showCallsData && this.showVoluumData
            ? DATA_SOURCE.MERGED
            : this.showCallsData
            ? DATA_SOURCE.CALLS
            : this.showVoluumData
            ? DATA_SOURCE.VOLUUM
            : null;

        if (!currentMode) return [];

        return Object.entries(columns)
          .filter(([key, config]) => config.sources.includes(currentMode))
          .map(([id, config]) => {
            const existingColumn = this.value.find(col => col.id === id);
            return {
              id,
              label: config.label,
              active: existingColumn ? existingColumn.active : true,
              required: config.required,
            };
          });
      },
    },
    mounted() {
      this.initializeColumns();
    },
    watch: {
      showCallsData() {
        this.initializeColumns();
      },
      showVoluumData() {
        this.initializeColumns();
      },
      value: {
        handler() {
          this.initializeColumns(false);
        },
        deep: true,
      },
    },
    methods: {
      triggerOpen() {
        this.isOpen = !this.isOpen;
      },
      toggleColumn(index) {
        this.columns[index].active = !this.columns[index].active;
        this.emitColumns();
      },
      initializeColumns(emit = true) {
        const modeColumns = this.availableColumns.map(col => {
          const parentColumn = this.value.find(c => c.id === col.id);
          return {
            ...col,
            active: parentColumn ? parentColumn.active : col.active,
          };
        });

        modeColumns.forEach(col => {
          if (col.required) col.active = true;
        });

        this.columns = modeColumns;
        if (emit) this.emitColumns();
      },
      emitColumns() {
        this.$emit("input", this.columns.slice());
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@/@core/preset/preset/typography/index.scss";

  .dynamic-columns-wrapper {
    border-radius: 4px;
    box-shadow: 0px 2px 10px rgba(69, 64, 78, 0.1);
    min-width: 250px;
  }

  .dynamic-columns-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #e0e0e0;
    font-weight: 600;
  }
  .dynamic-columns-list {
    padding: 0;
    max-height: 300px;
    overflow-y: auto;

    ::v-deep {
      .v-list-item {
        padding: 16px;
        height: unset !important;
        margin: 0 !important;
        cursor: pointer;

        &.v-list-item--active {
          background-color: get-color("primary-20");
        }

        &.v-list-item--disabled {
          cursor: not-allowed;
        }

        .v-input__slot {
          gap: 8px;
        }
      }

      .v-list-item__content {
        padding: 0;
      }
    }
  }

  .v-menu__content {
    @include text-body-2();
    color: #45404e !important;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
</style>
